import { Accordion, AccordionSummary, Box, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InputSwitch from '../../../../../components/Inputs/Switch';
import { SwitchSize } from '../../../../../components/Inputs/Switch/Switch';
import {
  useFilterDispatch,
  useFilterState,
} from '../../../shared/FinancialDataContext/FinancialDataContext';
import {
  FilterInputType,
  FinancialDataContextActionTypes,
  LibraryFilterItem,
} from '../../../shared/FinancialDataContext/FinancialDataContext.types';
import FilterIcon from './FilterIcon';
import { AddRemoveFilter } from './FilterLibrary';
import FilterLibraryFilterGroup from './FilterLibraryGroup';

interface Props {
  filterItem: LibraryFilterItem;
  path: number[];
}

const getEnumValue = (item: FilterInputType) => {
  return FilterInputType[item];
};

const isChecked = (appliedFilters: string[], labelKey: string) => {
  return appliedFilters.indexOf(labelKey) !== -1;
};

const FilterLibraryItem: React.FC<Props> = React.memo(({ filterItem, path }) => {
  const dispatch = useFilterDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const { appliedFilters, expandedLibraryFilters } = useFilterState();
  const [checked, setChecked] = useState(isChecked(appliedFilters, filterItem.labelKey));
  const isExpanded = useMemo(() => expandedLibraryFilters?.indexOf(filterItem.labelKey) !== -1, [
    expandedLibraryFilters,
    filterItem,
  ]);

  useEffect(() => {
    setChecked(isChecked(appliedFilters, filterItem.labelKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const renderedItem = useMemo(() => {
    return (
      <Accordion
        className={classes.filterItem}
        disabled={filterItem.disabled}
        square={true}
        data-cy={`filter-${filterItem.labelKey}`}
        data-type={filterItem.type ? `${getEnumValue(filterItem.type)}` : ''}
        expanded={isExpanded}
        onChange={
          !filterItem.type
            ? () =>
                dispatch({
                  type: FinancialDataContextActionTypes.ToggleLibraryFilter,
                  payload: filterItem.labelKey,
                })
            : () => {}
        }
      >
        <AccordionSummary expandIcon={!filterItem.type ? <ExpandMoreIcon /> : false}>
          {filterItem.type ? (
            <>
              <span className={classes.itemSpan}>
                {t(`companies:filters.${filterItem.labelKey}`)}
              </span>
              <InputSwitch
                fieldName={t(`companies:filters.${filterItem.labelKey}`)}
                formControlClassName={classes.itemSwitch}
                size={SwitchSize.small}
                checked={checked}
                onChange={(isAdded) =>
                  addOrRemoveFilter({
                    isFilterAdded: isAdded,
                    filter: filterItem,
                  })
                }
              />
            </>
          ) : (
            <Box component="div" className={classes.filterLabel}>
              {filterItem.icon ? <FilterIcon icon={filterItem.icon} /> : null}
              {t(`companies:filters.${filterItem.labelKey}`)}
            </Box>
          )}
        </AccordionSummary>
        {!filterItem.type && filterItem.children ? (
          <FilterLibraryFilterGroup
            items={filterItem.children}
            path={path}
            filterItem={filterItem}
          />
        ) : null}
      </Accordion>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, isExpanded]);

  return <>{renderedItem}</>;

  function addOrRemoveFilter({ isFilterAdded, filter }: AddRemoveFilter) {
    dispatch({
      type: !isFilterAdded
        ? FinancialDataContextActionTypes.Remove
        : FinancialDataContextActionTypes.Add,
      payload: filter.labelKey,
    });
  }
});

const useStyles = makeStyles((theme) => ({
  filterItem: {
    boxShadow: 'none',

    '&.Mui-expanded': {
      margin: '0',
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&:before': {
      backgroundColor: theme.palette.primary.contrastText,
    },

    '& .MuiAccordionSummary-content': {
      paddingLeft: '10px',
      alignItems: 'center',
      display: 'flex',
      margin: '10px 0',
      justifyContent: 'space-between',

      '& .MuiFormControl-root': {
        width: 'auto',
      },
    },
  },
  itemSpan: {
    width: '90%',
  },
  itemSwitch: {
    width: '10%',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default FilterLibraryItem;
